/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictProvider = require('pict-provider');

class DistillingBundleManager extends PictProvider
{
	/**
	 * @param {Pict} pFable - The Pict instance.
	 * @param {Record<string, any>} [pOptions] - (optional) The options for the provider.
	 * @param {string} [pServiceHash] - (optional) The service hash for the provider.
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;

		/** @type {import('fable') & import('pict')} */
		this.fable;

		/** @type {Record<string, any>} */
		this.options;

		this.fable.serviceManager.addServiceType('HeadlightRestClient', require(`headlight-rest-client`));

		if (!this.options.hasOwnProperty('HeadlightAPIURL'))
		{
			this.options.HeadlightAPIURL = '/1.0/';
		}
		this.fable.serviceManager.instantiateServiceProviderIfNotExists('HeadlightRestClient', { ServerURL: this.options.HeadlightAPIURL });
		this.pict.AppData._DistillingBundleData = {};
	}

	/**
	 * @param {(pError?: Error) => void} fCallback - The callback to call after data load is complete.
	 *
	 * @return {void}
	 */
	onLoadDataAsync(fCallback)
	{
		return super.onLoadDataAsync((pError) =>
		{
			if (pError)
			{
				return fCallback(pError);
			}

			const tmpManifest = this.pict.settings.MyDefaultFormManifest;
			if (!tmpManifest)
			{
				return fCallback();
			}
			this._primeBundlesFromManifest(tmpManifest, fCallback);
		});
	}

	/**
	 * @param {Record<string, any>} pManifest
	 * @param {(pError?: Error) => void} fCallback
	 */
	_primeBundlesFromManifest(pManifest, fCallback)
	{
		const tmpBundlesToLoad = {};
		for (const tmpDescriptor of Object.values(pManifest.Descriptors || {}))
		{
			if (tmpDescriptor.PictForm?.DistillingBundleHash)
			{
				//FIXME: support alternate source for project ID; ex. the loaded sample record
				tmpBundlesToLoad[tmpDescriptor.PictForm.DistillingBundleHash] = this.pict.AppData.DocumentData.ReportData.IDProject;
			}
		}
		const tmpPromises = [];
		if (Object.keys(tmpBundlesToLoad).length > 0)
		{
			for (const [ tmpBundleHash, tmpIDProject ] of Object.entries(tmpBundlesToLoad))
			{
				tmpPromises.push(this.getBundle(tmpBundleHash, tmpIDProject).catch((pError) =>
				{
					this.pict.log.error(`Error loading distilling bundle ${tmpBundleHash}: ${pError.message}`, { Stack: pError.stack });
				}));
			}
		}
		Promise.all(tmpPromises).finally(() => fCallback());
	}

	/**
	 * Hook on pre-solve to detect changes to required bundles.
	 *
	 * @param {(pError?: Error) => void} fCallback - The callback to call after pre-solve.
	 *
	 * @return {void}
	 */
	onPreSolveAsync(fCallback)
	{
		return super.onPreSolveAsync((pError) =>
		{
			if (pError)
			{
				return fCallback(pError);
			}
			// Ensure all bundles used in the current document are loaded
			const tmpManifest = this.pict.views.PictFormMetacontroller.manifestDefinition;
			if (!tmpManifest)
			{
				return fCallback();
			}
			this._primeBundlesFromManifest(tmpManifest, fCallback);
		});
	}

	/**
	 * @param {string} pBundleHash
	 * @param {number} [pIDProject]
	 *
	 * @return {Promise<any>}
	 */
	async getBundle(pBundleHash, pIDProject)
	{
		if (pIDProject == null)
		{
			pIDProject = this.pict.AppData.DocumentData.ReportData.IDProject;
		}
		let tmpBundle = this.pict.AppData._DistillingBundleData[pBundleHash]?.[pIDProject]?.Bundle;
		if (!tmpBundle)
		{
			tmpBundle = await this._loadBundle(pBundleHash, pIDProject);
		}
		return tmpBundle;
	}

	/**
	 * @param {string} pBundleHash
	 * @param {number} [pIDProject]
	 *
	 * @return {Grafee|null}
	 */
	getLatestGraph(pBundleHash, pIDProject)
	{
		let tmpData = this.pict.AppData._DistillingBundleData[pBundleHash]?.[pIDProject];
		if (!tmpData)
		{
			tmpData = this.pict.AppData._DistillingBundleData[pBundleHash]?.[this.pict.AppData._DistillingBundleData[pBundleHash]?.LatestProject];
		}
		if (!tmpData)
		{
			this.pict.log.warn(`Distilling bundle data for hash ${pBundleHash} not loaded yet.`);
			return null;
		}
		if (tmpData.Graph)
		{
			return tmpData.Graph;
		}
		if (tmpData.Bundle)
		{
			tmpData.Graph = new Grafee(tmpData.Bundle);
			return tmpData.Graph;
		}
		return null;
	}

	/**
	 * @param {string} pBundleHash
	 * @param {number} [pIDProject]
	 *
	 * @return {Promise<any>}
	 */
	async _loadBundle(pBundleHash, pIDProject)
	{
		this.pict.log.info(`Loading distilling bundle ${pBundleHash} for project ${pIDProject}`);
		const tmpBundle = await this._get(`Distilling/For/${pBundleHash}/Project/${pIDProject}`);
		this.pict.AppData._DistillingBundleData[pBundleHash] = this.pict.AppData._DistillingBundleData[pBundleHash] || {};
		this.pict.AppData._DistillingBundleData[pBundleHash].LatestProject = pIDProject;
		this.pict.AppData._DistillingBundleData[pBundleHash][pIDProject] = this.pict.AppData._DistillingBundleData[pBundleHash][pIDProject] || {};
		this.pict.AppData._DistillingBundleData[pBundleHash][pIDProject].Bundle = tmpBundle;
		//TODO: remove old bundles from AppData to release memory?
		return tmpBundle;
	}

	/**
	 * Perform a JSON GET request.
	 *
	 * @param {string} uri - The URI to get.
	 *
	 * @return {Promise<any>} - The response.
	 */
	async _get(uri)
	{
		return new Promise((resolve, reject) =>
		{
			this.pict.HeadlightRestClient.getJSON(uri, (pError, pBody) =>
			{
				if (pError)
				{
					return reject(pError);
				}
				resolve(pBody);
			});
		});
	}
}

module.exports = DistillingBundleManager;
module.exports.default_configuration = { AutoSaveDataWithApp: true };
